import { useCallback, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { Camera } from 'lucide-react';
import { Modal } from '../../components/feedback/Modal';
import { Button } from '../../components/actions/Button';
import { Avatar } from '../../components/data-display/Avatar';
import { getCroppedImg } from '../../utils/cropImage';
import { useDeleteAvatar, useUploadAvatar } from './queries';

/**
 * Avatar upload with a square crop. Ported from core's AvatarUploadModal; the
 * native-camera source is dropped — on desktop there is none, and on a phone
 * the file input already offers the camera.
 */
export function AvatarDialog({
  isOpen,
  onClose,
  currentUrl,
  name,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentUrl?: string;
  name: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [error, setError] = useState('');
  const upload = useUploadAvatar();
  const remove = useDeleteAvatar();
  const busy = upload.isPending || remove.isPending;

  const close = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setArea(null);
    setError('');
    onClose();
  };

  const onCropComplete = useCallback((_: Area, pixels: Area) => setArea(pixels), []);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!imageSrc || !area) return;
    setError('');
    try {
      const blob = await getCroppedImg(imageSrc, area);
      await upload.mutateAsync(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
      close();
    } catch {
      setError('The photo could not be uploaded. Try again.');
    }
  };

  const del = async () => {
    setError('');
    try {
      await remove.mutateAsync();
      close();
    } catch {
      setError('The photo could not be removed. Try again.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="sm"
      title="Profile photo"
      footer={
        <>
          {currentUrl && !imageSrc && (
            <Button variant="ghost-dark" size="sm" onClick={del} disabled={busy} className="mr-auto">
              Remove
            </Button>
          )}
          <Button variant="ghost-dark" size="sm" onClick={close}>
            Cancel
          </Button>
          {imageSrc ? (
            <Button size="sm" onClick={save} loading={upload.isPending} disabled={!area}>
              {upload.isPending ? 'Saving…' : 'Save'}
            </Button>
          ) : (
            <Button size="sm" onClick={() => fileRef.current?.click()}>
              Choose photo
            </Button>
          )}
        </>
      }
    >
      <div className="flex justify-center">
        <div className="relative h-[180px] w-[180px] overflow-hidden rounded-app bg-cream-warm">
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label="Choose a photo"
              className="group flex h-full w-full items-center justify-center border-0 bg-transparent"
            >
              <Avatar src={currentUrl} name={name} size="xl" />
              <span className="absolute inset-0 flex items-center justify-center bg-[rgba(42,22,9,.08)] text-brown-deep opacity-0 transition-opacity duration-fast group-hover:opacity-100">
                <Camera size={28} strokeWidth={2} aria-hidden="true" />
              </span>
            </button>
          )}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
      {error && (
        <p role="alert" className="mt-4 text-center text-[13px] text-danger">
          {error}
        </p>
      )}
    </Modal>
  );
}
